// src/shared/config/env.ts
import * as z from "zod";

const optionalNonEmptyString = (name: string) =>
	z.any().transform((input, ctx) => {
		// Treat "not provided" as OK
		if (input === undefined || input === null) return undefined;

		// Some CI setups can pass empty string; treat it as "not provided"
		if (typeof input === "string" && input.trim() === "") return undefined;

		if (typeof input !== "string") {
			ctx.addIssue({
				code: "custom",
				message: `${name} must be a string`,
				input,
			});
			return z.NEVER;
		}

		return input.trim();
	});
/**
 * Validates an environment variable that must be an HTTPS ORIGIN only.
 *
 * Rules:
 *  - required (must be a non-empty string)
 *  - must be a valid URL
 *  - must use https://
 *  - must NOT include path/query/hash (origin only)
 *
 * Output:
 *  - normalized origin string: "https://host[:port]"
 */
const httpsOrigin = (name: string) =>
	z.any().transform((input, ctx) => {
		// 1) Required + type check
		if (typeof input !== "string" || input.trim() === "") {
			ctx.addIssue({
				code: "custom",
				message: `${name} is required (example: https://movedapi.rein.ai)`,
				input,
			});
			return z.NEVER;
		}

		const value = input.trim();

		// 2) Parse as URL
		let url: URL;
		try {
			url = new URL(value);
		} catch {
			ctx.addIssue({
				code: "custom",
				message: `${name} must be a valid URL (example: https://movedapi.rein.ai)`,
				input: value,
			});
			return z.NEVER;
		}

		// 3) Enforce https
		if (url.protocol !== "https:") {
			ctx.addIssue({
				code: "custom",
				message: `${name} must use https://`,
				input: value,
			});
			return z.NEVER;
		}

		// 4) Enforce "origin only" (no path/query/hash)
		const hasExtra =
			url.pathname !== "/" || url.search.length > 0 || url.hash.length > 0;

		if (hasExtra) {
			ctx.addIssue({
				code: "custom",
				message: `${name} must be an ORIGIN only (no path/query/hash). Example: https://movedapi.rein.ai`,
				input: value,
			});
			return z.NEVER;
		}

		// 5) Normalize to origin
		return url.origin;
	});

/**
 * Single source of truth:
 * Add new env variables ONLY here to extend validation.
 */
const envShape = {
	API_ORIGIN: httpsOrigin("VITE_API_ORIGIN"),
	LOGROCKET_APP_ID: optionalNonEmptyString("VITE_LOGROCKET_APP_ID"),
	APPINSIGHTS_CONNECTION_STRING: optionalNonEmptyString("VITE_APPINSIGHTS_CONNECTION_STRING"),
	ASSURANT_SUBSCRIPTION_KEY: optionalNonEmptyString("VITE_ASSURANT_SUBSCRIPTION_KEY"),
} as const;

const envSchema = z.object(envShape);
export type Env = z.infer<typeof envSchema>;

/**
 * Picks only variables declared in envShape from import.meta.env.
 * This prevents accidentally relying on undeclared env vars.
 */
function readRawEnv(): Record<keyof typeof envShape, unknown> {
	const source = import.meta.env as Record<string, unknown>;
	const envKeyMap: Record<keyof typeof envShape, string> = {
		API_ORIGIN: "VITE_API_ORIGIN",
		LOGROCKET_APP_ID: "VITE_LOGROCKET_APP_ID",
		APPINSIGHTS_CONNECTION_STRING: "VITE_APPINSIGHTS_CONNECTION_STRING",
		ASSURANT_SUBSCRIPTION_KEY: "VITE_ASSURANT_SUBSCRIPTION_KEY",
	};
	return Object.fromEntries(
		Object.entries(envKeyMap).map(([k, sourceKey]) => [k, source[sourceKey]]),
	) as Record<keyof typeof envShape, unknown>;
}

/**
 * Parses and validates env at runtime.
 * If invalid, throws with a readable console output.
 */
export function parseEnv(): Env {
	const result = envSchema.safeParse(readRawEnv());
	if (!result.success) {
		const lines = result.error.issues.map((i) => {
			const key = i.path.join(".") || "(root)";
			return `${key}: ${i.message}`;
		});

		console.error("❌ Invalid environment variables:");
		for (const line of lines) console.error(`  - ${line}`);

		throw new Error("Invalid environment variables");
	}

	return result.data;
}

export const env = parseEnv();
